import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/form/button';
import { SearchBar } from '../../components/search-bar/search-bar';
import { Typography } from '../../components/typography/typography';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { DataTable, DataTableColumn, DataTableRow } from '../../components/table/data-table';
import { ReactNode, useContext, useState } from 'react';
import { QuipliLink } from '../../components/link/quipli-link';
import { TenantContext } from '../../context/tenant-context';
import { PaginationData } from '../../clients/quipli-service/pagination/pagination';
import { useVendorApi } from '../../clients/quipli-service/vendor/vendor-api';
import { Vendor, VendorSearchRequest } from '../../clients/quipli-service/vendor/vendor';
import { Dropdown } from '../../components/dropdown/dropdown';
import Icon from '../../components/icons/icon';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { toast } from '../../util/toasts';
import { VendorForm } from './vendor-form';

export const VendorList = () => {
  const { searchVendors } = useVendorApi();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();

  const [createModal, setCreateModal] = useState<ReactNode | undefined>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const columns: DataTableColumn[] = [
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Added On', key: 'date' },
    { key: 'actions', label: '', fixedAsLast: true },
  ];

  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<VendorSearchRequest>(
    {
      keyword: params.get('keyword') ?? '',
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async () => {
      setLoading(true);
      try {
        const { items, pagination } = await searchVendors({
          keyword: request.keyword,
          pageSize: request.pageSize,
          pageNumber: request.pageNumber,
        });

        setData(items.map(buildDataRow));
        setPagination(pagination);
      } catch (e: any) {
        toast(e.message.msg ?? e.message ?? 'Failed to fetch vendors', 'error');
      } finally {
        setLoading(false);
      }
    },
  );

  const buildDataRow = (r: Vendor): DataTableRow => {
    return {
      mobileHeader: (
        <>
          <div>
            <Typography type="text-sm" className="text-quipli-black-800">
              <QuipliLink
                to={`vendors/${r.id}`}
                className="underline underline-offset-1 text-[var(--quipli-black-800)]"
              >
                {r.name}
              </QuipliLink>
            </Typography>
          </div>
        </>
      ),
      fields: {
        name: (
          <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
            {r.name}
          </Typography>
        ),
        email: (
          <Typography type="text-sm" className="truncate text-quipli-black-800">
            {r.email || '-'}
          </Typography>
        ),
        phone: (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {r.phone || '-'}
            </Typography>
          </div>
        ),
        date: (
          <>
            <div>
              <Typography type="text-sm" className="text-quipli-black-800">
                {r.createdAt.toFormat('LLL-dd')}
              </Typography>
              <Typography type="text-sm" className="text-grey-500">
                {r.createdAt.toFormat('hh:mm a').toLowerCase()}
              </Typography>
            </div>
          </>
        ),
        actions: <VendorActions vendorId={r.id} reload={fetchCallback} />,
      },
      className: 'cursor-pointer',
      redirectTo: `vendors/${r.id}`,
    };
  };

  return (
    <div className="px-[10px] pb-[5px]">
      <Typography type="display-sm" weight="medium">
        Manage Vendors
      </Typography>

      <div className="pt-5 flex items-center justify-between">
        <SearchBar placeholder="Search vendors" defaultValue={request.keyword} setRequest={setRequest} />

        <div className="order-1 md:order2 flex gap-2">
          <Button
            className="button-yellow !border-0 h-[46px]"
            data-cy="create-vendor"
            onClick={() =>
              setCreateModal(
                <VendorForm
                  type={'modal'}
                  onSubmit={async (vendor) => {
                    navigate(`${contextPath}manager/vendors/${vendor.id}`);
                    toast('Vendor created.', 'success');
                  }}
                  onClose={() => setCreateModal(undefined)}
                />,
              )
            }
          >
            <Typography
              type="text-md"
              weight="semi-bold"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              Add New Vendor
            </Typography>
          </Button>
        </div>
      </div>

      <div className="pt-5">
        <DataTable
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          isLoading={isLoading}
        />
      </div>

      {createModal}
    </div>
  );
};

const VendorActions = ({ vendorId, reload }: { vendorId: number; reload: () => void }) => {
  const { deleteVendor } = useVendorApi();
  const [modal, setModal] = useState<ReactNode | undefined>();

  return (
    <div>
      <Dropdown
        placement="left-end"
        trigger={() => (
          <Button className="button-tertiary" data-cy={`customer_menu`}>
            <Icon icon="DotsVertical" />
          </Button>
        )}
      >
        <QuipliLink to={`vendors/${vendorId}`} data-cy="edit_vendor">
          <div className="flex gap-2">
            <Icon icon="Edit" />
            <Typography type="text-sm" className="text-quipli-black-800">
              Edit Details
            </Typography>
          </div>
        </QuipliLink>

        <div>
          <div
            className="flex gap-2"
            onClick={() =>
              setModal(
                <ConfirmModal
                  prompt="Delete vendor?"
                  additionalText="This cannot be undone"
                  noLabel="Cancel"
                  yesLabel="Delete"
                  yesButtonYellow
                  isOpen={true}
                  headerIcon={
                    <div className="flex justify-center">
                      <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                    </div>
                  }
                  onConfirm={async () => {
                    await deleteVendor(vendorId);
                    reload();
                    toast('Vendor deleted.', 'success');
                    setModal(undefined);
                  }}
                  close={() => setModal(undefined)}
                />,
              )
            }
            data-cy="delete_customer"
          >
            <Icon icon="Trash" stroke="var(--error-500)" />
            <Typography type="text-sm" className="text-error-500">
              Delete vendor
            </Typography>
          </div>
        </div>
      </Dropdown>

      {modal}
    </div>
  );
};
